<script setup>
import { reactive, computed, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, maxLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import AssistantCustomToolsAPI from 'dashboard/api/assistant/customTools';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import ParamRow from 'dashboard/components-next/assistant/shared/ParamRow.vue';
import AuthConfig from './AuthConfig.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  tool: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('assistantCustomTools/getUIFlags'),
};

const initialState = {
  title: '',
  description: '',
  slug: '',
  endpoint_url: '',
  http_method: 'GET',
  request_template: '',
  response_template: '',
  auth_type: 'none',
  auth_config: {},
  param_schema: [],
  enabled: true,
};

const state = reactive({ ...initialState });

// Populate form when in edit mode. auth_config is write-only on the backend,
// so the fields start empty and are only sent when refilled.
watch(
  () => props.tool,
  newTool => {
    if (props.mode === 'edit' && newTool && newTool.id) {
      state.title = newTool.title || '';
      state.description = newTool.description || '';
      state.slug = newTool.slug || '';
      state.endpoint_url = newTool.endpoint_url || '';
      state.http_method = newTool.http_method || 'GET';
      state.request_template = newTool.request_template || '';
      state.response_template = newTool.response_template || '';
      state.auth_type = newTool.auth_type || 'none';
      state.auth_config = {};
      state.param_schema = (newTool.param_schema || []).map(param => ({
        ...param,
      }));
      state.enabled = newTool.enabled ?? true;
    }
  },
  { immediate: true }
);

// Reset credentials whenever the auth type changes so stale keys from a
// previous type are never submitted.
watch(
  () => state.auth_type,
  () => {
    state.auth_config = {};
  }
);

const DEFAULT_PARAM = {
  name: '',
  type: 'string',
  description: '',
  required: false,
};

const MAX_TOOL_NAME_LENGTH = 55;

const validationRules = {
  title: { required, maxLength: maxLength(MAX_TOOL_NAME_LENGTH) },
  endpoint_url: { required },
  http_method: { required },
  auth_type: { required },
};

const httpMethodOptions = computed(() =>
  ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map(method => ({
    value: method,
    label: method,
  }))
);

const hasRequestBody = computed(() =>
  ['POST', 'PUT', 'PATCH'].includes(state.http_method)
);

const authTypeOptions = computed(() => [
  { value: 'none', label: t('ASSISTANT.TOOLS.FORM.AUTH_TYPES.NONE') },
  { value: 'bearer', label: t('ASSISTANT.TOOLS.FORM.AUTH_TYPES.BEARER') },
  { value: 'basic', label: t('ASSISTANT.TOOLS.FORM.AUTH_TYPES.BASIC') },
  { value: 'api_key', label: t('ASSISTANT.TOOLS.FORM.AUTH_TYPES.API_KEY') },
]);

const v$ = useVuelidate(validationRules, state);

const isLoading = computed(() =>
  props.mode === 'edit'
    ? formState.uiFlags.value.updatingItem
    : formState.uiFlags.value.creatingItem
);

const getErrorMessage = (field, errorKey) => {
  if (!v$.value[field].$error) return '';

  const failedRule = v$.value[field].$errors[0]?.$validator;
  if (failedRule === 'maxLength') {
    return t(`ASSISTANT.TOOLS.FORM.${errorKey}.MAX_LENGTH_ERROR`, {
      max: MAX_TOOL_NAME_LENGTH,
    });
  }
  return t(`ASSISTANT.TOOLS.FORM.${errorKey}.ERROR`);
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  endpoint_url: getErrorMessage('endpoint_url', 'ENDPOINT_URL'),
}));

const paramsRef = useTemplateRef('paramsRef');

const isParamsValid = () => {
  if (!paramsRef.value || paramsRef.value.length === 0) {
    return true;
  }
  return paramsRef.value.every(param => param.validate());
};

const removeParam = index => {
  state.param_schema.splice(index, 1);
};

const addParam = () => {
  state.param_schema.push({ ...DEFAULT_PARAM });
};

const generateSlug = title =>
  title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const hasAuthConfigValues = computed(() =>
  Object.values(state.auth_config).some(value => Boolean(value))
);

const prepareToolDetails = () => {
  const details = {
    title: state.title,
    description: state.description,
    slug: state.slug || generateSlug(state.title),
    endpoint_url: state.endpoint_url,
    http_method: state.http_method,
    request_template: state.request_template,
    response_template: state.response_template,
    auth_type: state.auth_type,
    param_schema: state.param_schema,
    enabled: state.enabled,
  };

  if (state.auth_type === 'none') {
    details.auth_config = {};
  } else if (props.mode === 'create' || hasAuthConfigValues.value) {
    details.auth_config = state.auth_config;
  }
  // In edit mode with empty fields auth_config is omitted so the backend
  // keeps the existing credentials.

  return details;
};

const handleCancel = () => emit('cancel');

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid || !isParamsValid()) {
    return;
  }

  emit('submit', prepareToolDetails());
};

// Test run
const isTesting = ref(false);
const testResult = ref(null);
const testParams = reactive({});

const namedParams = computed(() =>
  state.param_schema.filter(param => param.name?.trim())
);

const coerceParamValue = (value, type) => {
  if (value === '' || value === undefined) return undefined;
  if (type === 'number') return Number(value);
  if (type === 'boolean') return value === 'true' || value === '1';
  return value;
};

const buildTestParams = () => {
  return namedParams.value.reduce((params, param) => {
    const value = coerceParamValue(testParams[param.name], param.type);
    if (value !== undefined) params[param.name] = value;
    return params;
  }, {});
};

const formatBody = body => {
  if (body === null || body === undefined) return '';
  const text = typeof body === 'string' ? body : JSON.stringify(body, null, 2);
  return text.length > 2000 ? `${text.slice(0, 2000)}…` : text;
};

const handleTest = async () => {
  if (!state.endpoint_url) return;

  isTesting.value = true;
  testResult.value = null;
  try {
    const { data } = await AssistantCustomToolsAPI.test({
      customTool: prepareToolDetails(),
      testParams: buildTestParams(),
    });
    const isOk = data.status >= 200 && data.status < 300;
    testResult.value = {
      success: isOk,
      status: data.status,
      body: formatBody(data.body),
    };
  } catch (e) {
    const message = e.response?.data?.error || t('ASSISTANT.TOOLS.TEST.ERROR');
    testResult.value = { success: false, message };
  } finally {
    isTesting.value = false;
  }
};
</script>

<template>
  <form
    class="flex flex-col px-4 -mx-4 gap-4 max-h-[calc(100vh-200px)] overflow-y-scroll"
    @submit.prevent="handleSubmit"
  >
    <Input
      v-model="state.title"
      :label="t('ASSISTANT.TOOLS.FORM.TITLE.LABEL')"
      :placeholder="t('ASSISTANT.TOOLS.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <TextArea
      v-model="state.description"
      :label="t('ASSISTANT.TOOLS.FORM.DESCRIPTION.LABEL')"
      :placeholder="t('ASSISTANT.TOOLS.FORM.DESCRIPTION.PLACEHOLDER')"
      :rows="2"
    />

    <div class="flex gap-2">
      <div class="flex flex-col gap-1 w-28">
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t('ASSISTANT.TOOLS.FORM.HTTP_METHOD.LABEL') }}
        </label>
        <ComboBox
          v-model="state.http_method"
          :options="httpMethodOptions"
          class="[&>div>button]:bg-n-alpha-black2 [&_li]:font-mono [&_button]:font-mono [&>div>button]:outline-offset-[-1px]"
        />
      </div>
      <Input
        v-model="state.endpoint_url"
        :label="t('ASSISTANT.TOOLS.FORM.ENDPOINT_URL.LABEL')"
        :placeholder="t('ASSISTANT.TOOLS.FORM.ENDPOINT_URL.PLACEHOLDER')"
        :message="
          formErrors.endpoint_url ||
          t('ASSISTANT.TOOLS.FORM.ENDPOINT_URL.HELP_TEXT')
        "
        :message-type="formErrors.endpoint_url ? 'error' : 'info'"
        class="flex-1"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('ASSISTANT.TOOLS.FORM.AUTH_TYPE.LABEL') }}
      </label>
      <ComboBox
        v-model="state.auth_type"
        :options="authTypeOptions"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <AuthConfig
      v-if="state.auth_type !== 'none'"
      v-model:auth-config="state.auth_config"
      :auth-type="state.auth_type"
      :mode="mode"
    />

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('ASSISTANT.TOOLS.FORM.PARAMETERS.LABEL') }}
      </label>
      <p class="text-xs text-n-slate-11 -mt-1">
        {{ t('ASSISTANT.TOOLS.FORM.PARAMETERS.HELP_TEXT') }}
      </p>
      <ul v-if="state.param_schema.length > 0" class="grid gap-2 list-none">
        <ParamRow
          v-for="(param, index) in state.param_schema"
          :key="index"
          ref="paramsRef"
          v-model:name="param.name"
          v-model:type="param.type"
          v-model:description="param.description"
          v-model:required="param.required"
          @remove="removeParam(index)"
        />
      </ul>
      <Button
        type="button"
        sm
        ghost
        blue
        icon="i-lucide-plus"
        :label="t('ASSISTANT.TOOLS.FORM.ADD_PARAMETER')"
        @click="addParam"
      />
    </div>

    <TextArea
      v-if="hasRequestBody"
      v-model="state.request_template"
      :label="t('ASSISTANT.TOOLS.FORM.REQUEST_TEMPLATE.LABEL')"
      :placeholder="t('ASSISTANT.TOOLS.FORM.REQUEST_TEMPLATE.PLACEHOLDER')"
      :rows="4"
      :max-length="10000"
      class="[&_textarea]:font-mono"
    />

    <TextArea
      v-model="state.response_template"
      :label="t('ASSISTANT.TOOLS.FORM.RESPONSE_TEMPLATE.LABEL')"
      :placeholder="t('ASSISTANT.TOOLS.FORM.RESPONSE_TEMPLATE.PLACEHOLDER')"
      :rows="4"
      :max-length="10000"
      class="[&_textarea]:font-mono"
    />

    <label class="flex items-center gap-2 cursor-pointer">
      <Switch v-model="state.enabled" />
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('ASSISTANT.TOOLS.FORM.ENABLED.LABEL') }}
      </span>
    </label>

    <div
      class="flex flex-col gap-2 p-3 rounded-lg border border-n-weak bg-n-alpha-2"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('ASSISTANT.TOOLS.TEST.TITLE') }}
      </span>
      <template v-if="namedParams.length">
        <p class="text-xs text-n-slate-11">
          {{ t('ASSISTANT.TOOLS.TEST.PARAMS_HELP_TEXT') }}
        </p>
        <Input
          v-for="param in namedParams"
          :key="param.name"
          v-model="testParams[param.name]"
          :label="param.name"
          :placeholder="param.description || param.type"
        />
      </template>
      <Button
        type="button"
        variant="faded"
        color="slate"
        icon="i-lucide-play"
        :label="t('ASSISTANT.TOOLS.TEST.BUTTON')"
        :is-loading="isTesting"
        :disabled="isTesting || !state.endpoint_url"
        @click="handleTest"
      />
      <div
        v-if="testResult"
        class="flex flex-col gap-1 px-3 py-2 text-xs rounded-lg"
        :class="
          testResult.success
            ? 'bg-n-teal-2 text-n-teal-11'
            : 'bg-n-ruby-2 text-n-ruby-11'
        "
      >
        <span class="inline-flex items-center gap-2">
          <span
            :class="
              testResult.success ? 'i-lucide-check-circle' : 'i-lucide-x-circle'
            "
            class="size-3.5 shrink-0"
          />
          {{
            testResult.status
              ? t('ASSISTANT.TOOLS.TEST.STATUS', {
                  status: testResult.status,
                })
              : testResult.message
          }}
        </span>
        <div
          v-if="testResult.body"
          class="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-all font-mono text-xs"
        >
          {{ testResult.body }}
        </div>
      </div>
    </div>

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('ASSISTANT.FORM.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        type="submit"
        :label="
          t(mode === 'edit' ? 'ASSISTANT.FORM.EDIT' : 'ASSISTANT.FORM.CREATE')
        "
        class="w-full"
        :is-loading="isLoading"
        :disabled="isLoading"
      />
    </div>
  </form>
</template>
